import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit, startAfter, DocumentSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AdnReport } from '../../types/adnReport';
import { BarChart2, CalendarCheck, ChevronLeft, ChevronRight, Edit, Eye, Plus, Trash2, TrendingUp, Users, X } from 'lucide-react';
import { ReportFilterBar, ReportFilters, defaultFilters, isFiltersActive } from '../ui/ReportFilterBar';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { canEditReport, canDeleteReport } from '../../utils/permissions';
import AdnForm from './AdnForm';
import EditAdnReportForm from './EditAdnReportForm';
import { AdnReportPreview } from './AdnReportPreview';
import { AdnTrendChart } from './AdnTrendChart';
import { useAuth } from '../../contexts/AuthContext';
import { deleteDoc, doc } from 'firebase/firestore';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmContext';

export default function AdnReportsList() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [reports, setReports] = useState<AdnReport[]>([]);
  const [allReports, setAllReports] = useState<AdnReport[]>([]);
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters());
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AdnReport | null>(null);
  const [editingReport, setEditingReport] = useState<AdnReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const avgVisitors = allReports.length > 0 ? Math.round(allReports.reduce((s,r)=>s+(r.totalNewVisitors||0),0)/allReports.length) : 0;
  const totalWantsAll = allReports.reduce((s,r)=>s+(r.totalWantsToJoin||0),0);
  const totalVisitorsAll = allReports.reduce((s,r)=>s+(r.totalNewVisitors||0),0);
  const conversionRate = totalVisitorsAll > 0 ? Math.round((totalWantsAll/totalVisitorsAll)*100) : 0;
  const lastAdnDate = allReports.length > 0 ? [...allReports].sort((a,b)=>new Date(b.date).getTime()-new Date(a.date).getTime())[0]?.date : null;
  const [pageStartCursors, setPageStartCursors] = useState<Map<number, DocumentSnapshot>>(new Map());
  const reportsPerPage = 10;

  const [meetingTypesMap, setMeetingTypesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    getDocs(collection(db, 'meetingTypes')).then(snap => {
      const map: Record<string, string> = {};
      snap.forEach(d => { map[d.id] = (d.data() as { name?: string }).name ?? d.id; });
      setMeetingTypesMap(map);
    });
  }, []);

  const fetchReports = async (page = 1, direction: 'next' | 'prev' | 'direct' = 'direct', cursor?: DocumentSnapshot | null) => {
    try {
      setIsLoading(true);
      const reportsRef = collection(db, 'adnReports');
      
      // Charger le nombre total de rapports (seulement au premier chargement)
      if (page === 1 && direction === 'direct') {
        const countSnapshot = await getCountFromServer(query(reportsRef));
        setTotalReportsCount(countSnapshot.data().count);
        
        // Charger tous les rapports pour les statistiques et la recherche
        const allReportsQuery = query(reportsRef, orderBy('date', 'desc'));
        const allSnapshot = await getDocs(allReportsQuery);
        const allReportsData = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AdnReport[];
        setAllReports(allReportsData);
      }
      
      let q;
      
      if (direction === 'next' && cursor) {
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'prev' && cursor) {
        // Pour la page prÃ©cÃ©dente, on utilise endBefore avec un ordre inversÃ©
        q = query(reportsRef, orderBy('date', 'asc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'direct' && page > 1 && pageStartCursors.has(page)) {
        // Saut direct vers une page visitÃ©e
        const startCursor = pageStartCursors.get(page)!;
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(startCursor), limit(reportsPerPage + 1));
      } else {
        // Page 1 ou chargement initial
        q = query(reportsRef, orderBy('date', 'desc'), limit(reportsPerPage + 1));
      }
      
      const querySnapshot = await getDocs(q);
      let reportsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AdnReport[];
      
      // Si on navigue vers la page prÃ©cÃ©dente, inverser l'ordre
      if (direction === 'prev') {
        reportsData = reportsData.reverse();
      }
      
      // VÃ©rifier s'il y a plus de rapports
      const hasMore = reportsData.length > reportsPerPage;
      if (hasMore) {
        reportsData.pop(); // Retirer le rapport supplÃ©mentaire utilisÃ© pour la vÃ©rification
      }
      
      setReports(reportsData);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      setCurrentPage(page);
      
      // Mettre Ã  jour les curseurs de pagination
      if (reportsData.length > 0) {
        const newFirstDoc = querySnapshot.docs[0];
        const newLastDoc = querySnapshot.docs[reportsData.length - 1];
        
        setFirstDoc(newFirstDoc);
        setLastDoc(newLastDoc);
        
        // Sauvegarder le curseur de dÃ©but de cette page
        if (!pageStartCursors.has(page)) {
          setPageStartCursors(prev => new Map(prev).set(page, newFirstDoc));
        }
      }
    } catch (error) {
      console.error('Error fetching ADN reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const getTotalPages = () => Math.ceil(totalReportsCount / reportsPerPage);

  const goToNextPage = () => {
    if (hasNextPage && lastDoc) {
      const nextPage = currentPage + 1;
      fetchReports(nextPage, 'next', lastDoc);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage && firstDoc) {
      const prevPage = currentPage - 1;
      fetchReports(prevPage, 'prev', firstDoc);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber === 1) {
      setPageStartCursors(new Map()); // Reset cursors
      fetchReports(1, 'direct');
    } else if (pageStartCursors.has(pageNumber)) {
      fetchReports(pageNumber, 'direct');
    } else {
      // Pour les pages non visitÃ©es, redirection vers page 1
      console.warn(`Page ${pageNumber} non visitÃ©e, redirection vers page 1`);
      goToPage(1);
    }
  };

  const handleNewReportSubmit = async () => {
    setShowForm(false);
    // Retourner Ã  la page 1 aprÃ¨s ajout d'un nouveau rapport
    setCurrentPage(1);
    setPageStartCursors(new Map());
    fetchReports(1, 'direct');
  };

  const handleDelete = async (reportId: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Ãtes-vous sÃ»r de vouloir supprimer ce rapport ADN ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;

    try {
      await deleteDoc(doc(db, 'adnReports', reportId));
      // Recharger la page actuelle aprÃ¨s suppression
      fetchReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
    } catch (error) {
      console.error('Error deleting ADN report:', error);
      toast.error('Erreur lors de la suppression du rapport');
    }
  };

  const handleUpdateReport = () => {
    setEditingReport(null);
    // Recharger la page actuelle
    fetchReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
  };

  // Pour la recherche, utiliser tous les rapports
  const filteredReports = filters.search 
    ? allReports.filter(report => 
    new Date(report.date).toLocaleDateString('fr-FR').includes(filters.search) ||
    report.notes?.toLowerCase().includes(filters.search.toLowerCase())
  )
    : reports;

  const filtersActive = isFiltersActive(filters);

  // Utiliser tous les rapports pour les statistiques
  const statsReports = allReports.length > 0 ? allReports : reports;
  const totalPages = getTotalPages();


  return (

    <>
      {/* ===== MOBILE VIEW (< md) ===== */}
      <div className="md:hidden">
        <div className="min-h-screen bg-gray-50">
      {showForm && (
        <AdnForm onSubmit={handleNewReportSubmit} onCancel={() => setShowForm(false)} />
      )}
      {selectedReport && !showForm && !editingReport && (
        <AdnReportPreview report={selectedReport} onClose={() => setSelectedReport(null)} onEdit={(r) => { setSelectedReport(null); setEditingReport(r); }} />
      )}
      {editingReport && (
        <EditAdnReportForm report={editingReport} onClose={() => setEditingReport(null)} />
      )}
      {!showForm && !selectedReport && !editingReport && (
        <>
          <div className="bg-[#185FA5] text-white px-4 pt-10 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">ADN</h1>
                  <p className="text-white/70 text-sm">Gestion des rapports</p>
                </div>
              </div>
              <button onClick={() => setShowForm(true)} className="w-12 h-12 bg-vh-gold rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </button>
            </div>
            {totalReportsCount > 0 && (
              <div className="bg-white/10 rounded-xl p-3 flex items-center justify-center gap-2">
                <span className="text-2xl font-bold">{totalReportsCount}</span>
                <span className="text-white/70 text-sm">rapports au total</span>
              </div>
            )}
          </div>
          <div className="sticky top-0 z-10 bg-white shadow-sm px-4 py-2">
            <ReportFilterBar filters={filters} onChange={setFilters} />
          </div>
          {isLoading && <div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vh-green" /></div>}
          {!isLoading && (
            <div className="px-4 py-4 space-y-3">
              {filteredReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Users className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-base font-medium">Aucun rapport trouvÃ©</p>
                  <p className="text-sm mt-1">Appuyez sur + pour crÃ©er un rapport</p>
                </div>
              ) : (
                filteredReports.map(report => (
                  <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-stretch">
                      <div className="w-14 bg-vh-green flex flex-col items-center justify-center py-3 flex-shrink-0">
                        <span className="text-white font-bold text-xl leading-none">
                          {report.date?.toDate ? report.date.toDate().getDate() : new Date(report.date ?? 0).getDate()}
                        </span>
                        <span className="text-white/80 text-xs mt-0.5">
                          {report.date?.toDate ? report.date.toDate().toLocaleDateString('fr-FR', { month: 'short' }) : new Date(report.date ?? 0).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1 px-3 py-2.5 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm">{meetingTypesMap[report.meetingTypeId] ?? report.meetingTypeId}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-xs bg-vh-green/10 text-vh-green px-2 py-0.5 rounded-full font-medium">
                            {report.totalNewVisitors} visiteurs
                          </span>
                          {report.totalWantsToJoin > 0 && (
                            <span className="text-xs bg-vh-gold/10 text-vh-gold px-2 py-0.5 rounded-full font-medium">
                              {report.totalWantsToJoin} intÃ©grations
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col border-l border-gray-100 flex-shrink-0">
                        <button onClick={() => setSelectedReport(report)} className="flex-1 flex items-center justify-center px-3 text-blue-500 hover:bg-blue-50 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canEditReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => setEditingReport(report)} className="flex-1 flex items-center justify-center px-3 text-amber-500 hover:bg-amber-50 transition-colors">
                              <Edit className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {canDeleteReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => handleDelete(report.id)} className="flex-1 flex items-center justify-center px-3 text-red-400 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {(hasPrevPage || hasNextPage) && (
            <div className="flex items-center justify-between px-4 pb-6 pt-2">
              <button onClick={goToPrevPage} disabled={!hasPrevPage} className={hasPrevPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                <ChevronLeft className="w-4 h-4" /> PrÃ©cÃ©dent
              </button>
              <span className="text-sm text-gray-500 font-medium">Page {currentPage}</span>
              <button onClick={goToNextPage} disabled={!hasNextPage} className={hasNextPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
      </div>

      {/* ===== DESKTOP VIEW (>= md) ===== */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">ADN</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(!showForm)}
            className={`inline-flex items-center px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 transition-colors ${
              showForm 
                ? 'bg-gray-600 text-white hover:bg-gray-700' 
                : 'bg-[#00665C] text-white hover:bg-[#004d45]'
            }`}
          >
            {showForm ? (
              <>
                <X size={20} className="mr-2" />
                Fermer le formulaire
              </>
            ) : (
              <>
                <Plus size={20} className="mr-2" />
                Nouveau rapport ADN
              </>
            )}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
          <AdnForm onSubmit={handleNewReportSubmit} />
        </div>
      )}

      {/* Graphiques de tendances */}
      {statsReports.length > 0 && (
        <>
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#0F6E56]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Rapports</h3>
              <BarChart2 className="h-4 w-4 text-[#0F6E56] opacity-70" />
            </div>
            <p className="text-2xl font-bold text-[#0F6E56]">{totalReportsCount}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#185FA5]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Visiteurs moy.</h3>
              <Users className="h-4 w-4 text-[#185FA5] opacity-70" />
            </div>
            <p className="text-2xl font-bold text-[#185FA5]">{avgVisitors}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#534AB7]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Taux conversion</h3>
              <TrendingUp className="h-4 w-4 text-[#534AB7] opacity-70" />
            </div>
            <p className="text-2xl font-bold text-[#534AB7]">{conversionRate}%</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#BA7517]">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dernier rapport</h3>
              <CalendarCheck className="h-4 w-4 text-[#BA7517] opacity-70" />
            </div>
            <p className="text-lg font-bold text-gray-900">{lastAdnDate ? new Date(lastAdnDate).toLocaleDateString('fr-FR') : '\u2014'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <AdnTrendChart reports={statsReports} />
        </div>
        </>
      )}

      <div className="mb-3">
        <h2 className="text-xl font-bold text-gray-900">Rapports ADN</h2>
        <p className="text-sm text-gray-600 mt-1">
          {filteredReports.length} rapport{filteredReports.length !== 1 ? 's' : ''} au total
        </p>
      </div>
      {/* Liste des rapports */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex-1">
            <ReportFilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(defaultFilters())}
              placeholder="Rechercher par date ou notes..."
              resultCount={filteredReports.length}
              totalCount={allReports.length || totalReportsCount}
              meetingTypeSlot={
                <MeetingTypeSelect value={filters.meetingTypeId ?? ''} onChange={v => setFilters(f => ({ ...f, meetingTypeId: v }))} required={false} />
              }
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#00665C]/25 border-t-[#00665C]"></div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Users size={48} className="mb-4" />
            <p className="text-lg font-medium">Aucun rapport ADN trouvÃ©</p>
            <p className="text-sm">CrÃ©ez votre premier rapport ADN</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Mobile view: Cards */}
            <div className="lg:hidden space-y-4">
              {filteredReports.map((report) => (
                <div key={report.id} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-sm text-gray-500">
                        {new Date(report.date).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="font-medium">Total visiteurs: {report.totalNewVisitors}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="text-[#00665C] hover:text-[#004d45] transition-colors"
                        title="PrÃ©visualiser le rapport"
                      >
                        <Eye size={18} />
                      </button>
                      {canEditReport(report, user) && (
                        <button
                          onClick={() => setEditingReport(report)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Modifier le rapport"
                        >
                          <Edit size={18} />
                        </button>
                      )}
                      {canDeleteReport(report, user) && (
                        <button
                          onClick={() => handleDelete(report.id!)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Supprimer le rapport"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div>H: {report.newVisitors.men} | F: {report.newVisitors.women}</div>
                    <div>IndÃ©cis: {report.visitorDecisions.undecided}</div>
                    <div className="flex items-center">
                      Veulent rejoindre: 
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {report.totalWantsToJoin}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop view: Table */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nouveaux visiteurs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ratio H/F</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IndÃ©cis</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veulent rejoindre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(report.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{report.totalNewVisitors} visiteur{report.totalNewVisitors !== 1 ? 's' : ''}</span></td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 min-w-[110px]">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
                            <div className="h-full bg-[#185FA5] rounded-l-full transition-all" style={{width: `${report.totalNewVisitors > 0 ? Math.round((report.newVisitors.men / report.totalNewVisitors) * 100) : 50}%`}} />
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{report.newVisitors.men}H/{report.newVisitors.women}F</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{report.visitorDecisions.undecided > 0 ? (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{report.visitorDecisions.undecided} indÃ©cis</span>) : (<span className="text-gray-300 text-sm font-medium">â</span>)}</td>
                      <td className="px-6 py-4">{report.totalWantsToJoin > 0 ? (<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">â {report.totalWantsToJoin} rejoin{report.totalWantsToJoin > 1 ? 'tent' : 't'}</span>) : (<span className="text-gray-300 text-sm font-medium">â</span>)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedReport(report)}
                            className="text-[#00665C] hover:text-[#004d45] transition-colors"
                            title="PrÃ©visualiser le rapport"
                          >
                            <Eye size={18} />
                          </button>
                          {canEditReport(report, user) && (
                            <button
                              onClick={() => setEditingReport(report)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Modifier le rapport"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                          {canDeleteReport(report, user) && (
                            <button
                              onClick={() => handleDelete(report.id!)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Supprimer le rapport"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {!filtersActive && filteredReports.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} sur {totalPages} â¢ {totalReportsCount} rapports au total
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Bouton PrÃ©cÃ©dent */}
              <button
                onClick={goToPrevPage}
                disabled={!hasPrevPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasPrevPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                PrÃ©cÃ©dent
              </button>
              
              {/* NumÃ©ros de page */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers: (number | string)[] = [];
                  
                  // Toujours inclure la page 1
                  if (totalPages > 0) {
                    pageNumbers.push(1);
                  }
                  
                  // Ajouter des ellipses si nÃ©cessaire avant la page actuelle
                  if (currentPage > 4) {
                    pageNumbers.push('...');
                  }
                  
                  // Ajouter les pages autour de la page actuelle
                  const startPage = Math.max(2, currentPage - 1);
                  const endPage = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = startPage; i <= endPage; i++) {
                    if (!pageNumbers.includes(i)) {
                      pageNumbers.push(i);
                    }
                  }
                  
                  // Ajouter des ellipses si nÃ©cessaire aprÃ¨s la page actuelle
                  if (currentPage < totalPages - 3) {
                    pageNumbers.push('...');
                  }
                  
                  // Toujours inclure la derniÃ¨re page si elle n'est pas dÃ©jÃ  incluse
                  if (totalPages > 1 && !pageNumbers.includes(totalPages)) {
                    pageNumbers.push(totalPages);
                  }
                  
                  return pageNumbers.map((pageNum, index) => {
                    if (pageNum === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum as number)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          pageNum === currentPage
                            ? 'bg-[#00665C] text-white'
                            : 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  });
                })()}
              </div>
              
              {/* Bouton Suivant */}
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasNextPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {selectedReport && (
        <AdnReportPreview
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onEdit={(report) => {
            setSelectedReport(null);
            setEditingReport(report);
          }}
        />
      )}

      {/* Edit Form Modal */}
      {editingReport && (
        <EditAdnReportForm
          report={editingReport}
          onClose={() => setEditingReport(null)}
          onUpdate={handleUpdateReport}
        />
      )}
    </div>
      </div>
    </>
  );
}
